// Plan 14-05 (MUI-05) — FindableWordOutline.
//
// SVG overlay that renders Sun-Yellow stroked rectangles around the cells
// in a server-suggested findable word path. Anchored above the MatchCanvas
// + below the WordTraceLayer so it does not block user pointer events
// (pointerEvents:'none' is the contract).
//
// Server is the authority for "what word is findable right now"
// (DEC-021) — this component is purely a renderer for the cell index set
// it receives via props. The MatchScreen wires the data source.
//
// UI-SPEC §"Accent reserved for #6" — Sun-Yellow accent is permitted here
// as a coach-affordance hint. Rounded corners use a 6% radius ratio per
// the chunky-card token discipline.

import type { JSX } from 'react'
import { useIntl } from 'react-intl'

export interface FindableWordOutlineProps {
  /**
   * Cell indices (0..35) to outline. Empty array → component renders nothing
   * (defensive — the consumer is expected to gate the render upstream, but
   * an empty array is also a valid "no findable word right now" signal).
   */
  readonly cells: readonly number[]
  /** Per-cell pixel size of the 6×6 grid (e.g. 60 for a 360px board). */
  readonly cellSize: number
  /** Board offset relative to the SVG origin (defaults to 0). */
  readonly boardOffsetX?: number
  readonly boardOffsetY?: number
}

/** UI-SPEC §"chunky-card" corner radius ratio. */
const CORNER_RADIUS_RATIO = 0.06

export function FindableWordOutline({
  cells,
  cellSize,
  boardOffsetX = 0,
  boardOffsetY = 0,
}: FindableWordOutlineProps): JSX.Element | null {
  const intl = useIntl()
  if (cells.length === 0) return null
  const radius = cellSize * CORNER_RADIUS_RATIO
  return (
    <svg
      role="img"
      aria-label={intl.formatMessage({
        id: 'match.grid.findable_word_label',
        defaultMessage: 'Coach suggestion: traceable word',
      })}
      data-screen-region="findable-word-outline"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
    >
      {cells.map((idx) => {
        const r = Math.floor(idx / 6)
        const c = idx % 6
        return (
          <rect
            key={idx}
            x={boardOffsetX + c * cellSize}
            y={boardOffsetY + r * cellSize}
            width={cellSize}
            height={cellSize}
            fill="none"
            stroke="var(--color-cta-primary)"
            strokeWidth={3}
            rx={radius}
            ry={radius}
          />
        )
      })}
    </svg>
  )
}
